import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getAdminStats } from '../lib/api';
import { Calendar, Package, MessageSquare, Star, Clock, CheckCircle } from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await getAdminStats();
        setStats(data);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando estadísticas...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Citas de Hoy',
      value: stats?.appointments?.today || 0,
      icon: Clock,
      color: 'bg-primary/10 text-primary'
    },
    {
      title: 'Citas Pendientes',
      value: stats?.appointments?.pending || 0,
      icon: Calendar,
      color: 'bg-warning/10 text-warning'
    },
    {
      title: 'Total Citas',
      value: stats?.appointments?.total || 0,
      icon: CheckCircle,
      color: 'bg-success/10 text-success'
    },
    {
      title: 'Productos Activos',
      value: stats?.products?.active || 0,
      icon: Package,
      color: 'bg-sage-300/20 text-sage-500'
    },
    {
      title: 'Reseñas Pendientes',
      value: stats?.reviews?.pending || 0,
      icon: MessageSquare,
      color: 'bg-accent/10 text-accent'
    },
    {
      title: 'Calificación Promedio',
      value: stats?.reviews?.avg_rating || '0.0',
      icon: Star,
      color: 'bg-warning/10 text-warning'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">Bienvenido al panel de administración</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <Card key={index} className="border border-border" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                  <p className="text-3xl font-bold text-foreground">{card.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${card.color}`}>
                  <card.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-heading">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <a href="/admin/productos" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-foreground">Gestionar Productos</p>
                  <p className="text-sm text-muted-foreground">Agregar, editar o eliminar servicios</p>
                </div>
              </div>
            </a>
            <a href="/admin/citas" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-foreground">Ver Citas</p>
                  <p className="text-sm text-muted-foreground">Confirmar o cancelar citas</p>
                </div>
              </div>
            </a>
            <a href="/admin/resenas" className="block p-4 bg-sage-50 rounded-xl hover:bg-sage-100 transition-colors">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-foreground">Moderar Reseñas</p>
                  <p className="text-sm text-muted-foreground">Aprobar o rechazar comentarios</p>
                </div>
              </div>
            </a>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-heading">Información del Sistema</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Total Productos</span>
              <span className="font-medium">{stats?.products?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-muted-foreground">Total Reseñas</span>
              <span className="font-medium">{stats?.reviews?.total || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Total Citas</span>
              <span className="font-medium">{stats?.appointments?.total || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
